import { CanonicalSearchLoad, DispatchHelpMessage } from '@numeo/types'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { BookCheck, Check, CheckCheck, ChevronLeft, Gavel, Headset, Info, Mail, MessagesSquare, X } from 'lucide-react'
import { Button } from 'components/ds/Button'
import { IconButton } from 'components/ds'
import { Spinner } from 'components/ds/Spinner'
import { useAuth } from 'hooks/auth'
import { useAppDispatch, useAppSelector } from 'hooks'
import { showWarning } from 'utils/snackbar'
import { renderAgentMarkdown } from '@/components/agent-markdown/renderAgentMarkdown'
import { fetchSessionMessages, handbackSession, sendDispatcherMessage, takeoverSession } from '../services/dispatchHelpApi'
import { appendMessage, prependOlderMessages, reconcileMessage, removeMessage, setLoadingOlderMessages, setTakeover } from '../store/dispatchHelpSlice'
import { scheduleReplyRefetch } from '../store/socket-handlers'
import { sessionOwnerLabel } from '../sessionLabel'
import { EmailsPanel } from './EmailsPanel'
import { OwnerSessionsPanel } from './OwnerSessionsPanel'
import { SessionOwnerCard } from './SessionOwnerCard'
import { BrokerCallLogCard } from './BrokerCallLogCard'
import { MessageComposer } from './MessageComposer'
import { ThreadLoads } from './ThreadLoads'
import { USER_BUBBLE_TONE, UserInputKindLabel } from './UserInputKindLabel'
import { VoiceCallCard } from './VoiceCallCard'

// Load actions the operator can fire on the selected loads — sent as an @numeoai prompt so the agent
// runs them in the TAB OWNER's context (emails the broker etc.), exactly as the driver would on mobile.
const LOAD_ACTIONS = [
    { label: 'Book this load', Icon: BookCheck },
    { label: 'Ask info', Icon: Info },
    { label: 'Bid on this load', Icon: Gavel },
] as const

let actionSeq = 0

function cityState(city?: string | null, st?: string | null): string {
    return [city, st].filter(Boolean).join(', ') || '—'
}

function Bubble({ message, tabId, readMark }: { message: DispatchHelpMessage; tabId: string; readMark?: string }) {
    if (message.role === 'voice-session' && message.voiceSession) {
        return <VoiceCallCard voiceSession={message.voiceSession} tabId={tabId} messageId={message.id} />
    }
    if (message.role === 'broker-call' && message.brokerCall) {
        return <BrokerCallLogCard card={message.brokerCall} tabId={tabId} />
    }
    if (message.role === 'system') {
        // Presence/status notices (a dispatcher joined, handed back to the AI) — a subtle centered chip,
        // not a floating line, so they read as system events without competing with the conversation.
        return (
            <div className="my-0.5 flex justify-center">
                <span className="max-w-[85%] rounded-full bg-slate-100 px-3 py-1 text-center text-[11px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    {message.text}
                </span>
            </div>
        )
    }
    if (message.role === 'user') {
        return (
            <div className="flex flex-col items-end gap-0.5">
                {/* Directed at the human (via @dispatcher or a reply) — the AI was skipped for this turn. */}
                {message.wantsDispatcher && (
                    <div className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-300">
                        <Headset className="h-3 w-3" /> To dispatcher
                    </div>
                )}
                <UserInputKindLabel kind={message.inputKind ?? 'typed'} />
                <div className={`max-w-[85%] rounded-2xl rounded-br-sm ${USER_BUBBLE_TONE[message.inputKind ?? 'typed']} px-3.5 py-2 text-sm shadow-sm`}>
                    {/* Tap-to-reply quote: the exact dispatcher note the driver replied to. Drawn in the
                        bubble's own text colour, so it reads on every tone. */}
                    {message.replyTo && (
                        <div className="mb-1 rounded border-l-2 border-current/40 bg-current/10 px-2 py-1 text-xs opacity-90">
                            <div className="font-semibold">↩ {message.replyTo.authorName || 'Dispatcher'}</div>
                            <div className="line-clamp-2 whitespace-pre-wrap opacity-90">{message.replyTo.text}</div>
                        </div>
                    )}
                    <div className="whitespace-pre-wrap">{message.text}</div>
                </div>
            </div>
        )
    }
    if (message.role === 'dispatcher') {
        // Read ticks (Telegram-style): the driver has seen this note once their read high-water mark
        // reaches its timestamp. Gate on a SERVER-persisted row: an optimistic (local-*) row carries the
        // admin's browser-clock createdAt, not the DB one, so — if that clock lags the server — comparing
        // it to the server-derived readMark could show a false ✓✓ before the note even reaches the driver.
        // It shows ✓ until it reconciles to its server row. ISO-UTC strings compare lexicographically.
        const persisted = !message.id.startsWith('local-')
        const read = persisted && !!readMark && !!message.createdAt && message.createdAt <= readMark
        return (
            <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-emerald-50 px-3.5 py-2 text-sm text-emerald-900 shadow-sm ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-100 dark:ring-emerald-500/30">
                    <div className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-300">
                        <Headset className="h-3 w-3" />
                        {message.authorName || 'Dispatcher'}
                    </div>
                    <div className="whitespace-pre-wrap">{message.text}</div>
                    <div className="mt-0.5 flex justify-end" title={read ? 'Read' : 'Sent'}>
                        {read ? <CheckCheck className="h-3.5 w-3.5 text-blue-500" /> : <Check className="h-3.5 w-3.5 text-emerald-500/60" />}
                    </div>
                </div>
            </div>
        )
    }
    // agent — assistant text spans the reading column (left-aligned, no bubble), like a chat assistant.
    return (
        <div className="flex justify-start">
            <div className="w-full text-sm leading-relaxed text-slate-700 dark:text-slate-200">{renderAgentMarkdown(message.text, { loadRefJump: true })}</div>
        </div>
    )
}

export function ChatThread({ onBack, onSelectSession }: { onBack?: () => void; onSelectSession?: (tabId: string) => void }) {
    const dispatch = useAppDispatch()
    const { user } = useAuth()
    const tabId = useAppSelector((s) => s.dispatchHelp.activeSessionId)
    const session = useAppSelector((s) => (tabId ? s.dispatchHelp.sessions[tabId] : undefined))
    const messages = useAppSelector((s) => (tabId ? s.dispatchHelp.messagesByTab[tabId] : undefined))
    const readMark = useAppSelector((s) => (tabId ? s.dispatchHelp.readMarkByTab[tabId] : undefined))
    // What the agent is doing right now — the operator's version of the phone's searching/scoring line.
    const agentActivity = useAppSelector((s) => (tabId ? s.dispatchHelp.agentActivityByTab[tabId] : undefined))
    const loading = useAppSelector((s) => s.dispatchHelp.loadingMessages)
    const hasOlder = useAppSelector((s) => (tabId ? (s.dispatchHelp.hasOlderMessagesByTab[tabId] ?? false) : false))
    const oldestPage = useAppSelector((s) => (tabId ? (s.dispatchHelp.messagesPageByTab[tabId] ?? 1) : 1))
    const loadingOlder = useAppSelector((s) => s.dispatchHelp.loadingOlderMessages)
    const [busy, setBusy] = useState(false)
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [actionBusy, setActionBusy] = useState(false)
    const [emailsOpen, setEmailsOpen] = useState(false)
    const [otherSessionsOpen, setOtherSessionsOpen] = useState(false)
    const bottomRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    // Is the operator pinned to the bottom, i.e. should the thread follow new turns? Same intent flag
    // ChatPill uses. It answers both "don't chase the newest message after loading history" and
    // "don't yank someone reading 300 turns up when a live message lands".
    const atBottomRef = useRef(true)
    const lastScrollTopRef = useRef(0)
    // Pre-merge scroll height for an in-flight older page, stamped with the tab it was measured on —
    // switching sessions mid-fetch would otherwise apply this thread's delta to a different one.
    const anchorRef = useRef<{ height: number; tabId: string; firstId: string | null } | null>(null)
    const loadingOlderRef = useRef(false)

    // Every load shown in the thread, de-duped by id — the pool the multi-select + actions draw from.
    // (A load can appear in more than one turn; without the dedupe an action composes a line per copy.)
    const allLoads = useMemo<CanonicalSearchLoad[]>(() => {
        const byId = new Map<string, CanonicalSearchLoad>()
        for (const m of messages ?? []) for (const l of m.loads ?? []) byId.set(l.id, l)
        return [...byId.values()]
    }, [messages])

    // Opening a session starts pinned to the newest turn; that is also what resets the flag between
    // sessions. Declared BEFORE the follow effect so the reset lands first on a tab switch.
    useEffect(() => {
        atBottomRef.current = true
        lastScrollTopRef.current = 0
        anchorRef.current = null
    }, [tabId])

    // Follow new turns only while the operator is at the bottom. A guard keyed off the prepend was not
    // enough: React runs layout effects during commit and passive effects after paint, so the layout
    // effect below had already cleared the flag by the time this ran — the anchor was applied and then
    // immediately undone by a jump to the newest message.
    useEffect(() => {
        // Their own send re-engages the follow, however far up they were reading — otherwise they hit
        // send and nothing visibly happens. Optimistic rows are the signal (`local-*`, from the
        // composer and the load actions); ChatPill does the same off its `isSending` flag.
        const last = messages?.[messages.length - 1]
        if (last?.id.startsWith('local-')) atBottomRef.current = true
        if (!atBottomRef.current) return
        bottomRef.current?.scrollIntoView({ block: 'end' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages?.length, tabId])

    /** Pull the next older page and prepend it. The server pages NEWEST-first, so page N+1 is the
     *  block before what we already hold. */
    async function loadOlder() {
        if (!tabId || !hasOlder || loadingOlderRef.current) return
        loadingOlderRef.current = true
        anchorRef.current = { height: scrollRef.current?.scrollHeight ?? 0, tabId, firstId: messages?.[0]?.id ?? null }
        dispatch(setLoadingOlderMessages(true))
        try {
            const page = oldestPage + 1
            const { messages: older, total } = await fetchSessionMessages(tabId, page)
            dispatch(prependOlderMessages({ tabId, messages: older, page, total }))
        } catch {
            anchorRef.current = null // nothing merged — leave the scroll position alone
            // Without this the spinner blinks and nothing moves: indistinguishable from reaching the
            // top of the thread, and the next scroll silently retries.
            showWarning('Could not load older messages — scroll up again to retry.')
        } finally {
            loadingOlderRef.current = false
            dispatch(setLoadingOlderMessages(false))
        }
    }

    // Near the top and there is more history → fetch it. 120px of lead-in so the older turns are
    // usually in place by the time the operator reaches the edge.
    function onScroll() {
        const el = scrollRef.current
        if (!el) return
        const { scrollTop, scrollHeight, clientHeight } = el
        // Any upward movement disengages the follow; coming back to the bottom re-engages it (covers
        // scrollbar and keyboard, which emit no wheel events).
        if (scrollTop < lastScrollTopRef.current - 2) atBottomRef.current = false
        else if (scrollHeight - scrollTop - clientHeight < 40) atBottomRef.current = true
        lastScrollTopRef.current = scrollTop
        if (scrollTop < 120) void loadOlder()
    }

    // Hold the reading position across a prepend: the thread grew upward, so scroll down by exactly the
    // height that was added. A layout effect, not an effect — it must run before paint, or the view
    // visibly jumps to the top of the block that just loaded.
    useLayoutEffect(() => {
        const el = scrollRef.current
        const pending = anchorRef.current
        if (!el || !pending) return
        if (pending.tabId !== tabId) {
            anchorRef.current = null // session switched mid-fetch — that delta is another thread's
            return
        }
        // A live turn can land while the older page is still in flight. It grows the thread at the
        // BOTTOM, so the pending delta doesn't apply — and consuming it here would leave the older
        // page unanchored when it arrives. Only the head changing means history was prepended.
        if ((messages?.[0]?.id ?? null) === pending.firstId) return
        anchorRef.current = null
        el.scrollTop += el.scrollHeight - pending.height
    }, [messages, tabId])

    // Reset per-session UI when switching sessions.
    useEffect(() => {
        setSelectedIds(new Set())
        setEmailsOpen(false)
    }, [tabId])

    if (!tabId || !session) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-slate-400 dark:text-slate-500">Select a session to view the conversation</div>
        )
    }

    const takeover = session.takeover
    const mine = !!takeover && takeover.dispatcherId === user?.id
    const dispatcherName = user?.name || user?.email || 'Dispatcher'

    function toggleSelect(id: string) {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    // Fire a load action: compose the same "For load … <action>" prompt the mobile sends, then run it
    // through the AI (invokeAi) so the agent acts in the tab owner's context. Optimistic + refetch,
    // mirroring MessageComposer. Works without takeover — it's the AI acting, not a note to the driver.
    async function sendAction(action: string) {
        if (!tabId || actionBusy) return
        // Acting on a load writes into the conversation — require takeover first (nudge, don't send).
        if (!mine) {
            showWarning('Take over the conversation before acting on a load', undefined, { title: 'Take over', onClick: onTakeover })
            return
        }
        const selected = allLoads.filter((l) => selectedIds.has(l.id))
        if (!selected.length) return
        const lines = selected.map(
            (l) =>
                `For load ${l.brokerCompanyName ?? '—'} #${l.rowNumber} from ${cityState(l.pickupCity, l.pickupStateProv)} to ${cityState(l.deliveryCity, l.deliveryStateProv)}`
        )
        const text = `${lines.join('\n\n')}\n\n${action}`
        const tempId = `local-action-${++actionSeq}`
        setActionBusy(true)
        dispatch(appendMessage({ tabId, message: { id: tempId, role: 'dispatcher', text, authorName: dispatcherName, createdAt: new Date().toISOString() } }))
        setSelectedIds(new Set())
        try {
            const saved = await sendDispatcherMessage(tabId, text, dispatcherName, true)
            dispatch(reconcileMessage({ tabId, tempId, message: saved }))
            scheduleReplyRefetch(tabId, dispatch)
        } catch {
            dispatch(removeMessage({ tabId, id: tempId }))
        } finally {
            setActionBusy(false)
        }
    }

    async function onTakeover() {
        if (!tabId) return
        setBusy(true)
        try {
            const t = await takeoverSession(tabId, dispatcherName)
            dispatch(setTakeover({ tabId, takeover: t }))
        } finally {
            setBusy(false)
        }
    }

    async function onHandback() {
        if (!tabId) return
        setBusy(true)
        try {
            await handbackSession(tabId)
            dispatch(setTakeover({ tabId, takeover: null }))
        } finally {
            setBusy(false)
        }
    }

    // Real conversation turns vs. only presence banners (dispatcher joined / handed back) — drives the
    // empty hint + vertical centering so a chat-less session reads as intentional, not broken.
    const hasRealContent = (messages ?? []).some((m) => m.role !== 'system' || m.loads?.length)

    return (
        <div className="relative flex h-full flex-col">
            {/* Header — name + email on top; the Emails action (left) and Take over button (right) sit on
                their own row below, spread apart. */}
            <div className="border-b border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center gap-2">
                    {/* Back to the session list — mobile only (desktop shows both panes side by side). */}
                    {onBack && (
                        <span className="-ml-1 shrink-0 md:hidden">
                            <IconButton variant="ghost" size="sm" icon={<ChevronLeft className="h-5 w-5" />} onClick={onBack} aria-label="Back to sessions" />
                        </span>
                    )}
                    <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold uppercase text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 sm:flex">
                        {(session.senderEmail || session.title || '?').trim().charAt(0) || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{session.title || 'Conversation'}</div>
                        <div className="truncate text-xs text-slate-500 dark:text-slate-400">{sessionOwnerLabel(session)}</div>
                    </div>
                </div>
                {/* Full owner context — account, phone, connected mailboxes, virtual mailbox. */}
                <SessionOwnerCard tabId={session.tabId} />
                {/* Actions row — Emails on the left, Take over / Hand back on the right. */}
                <div className="mt-2 flex items-center justify-between gap-2">
                    {/* Labelled, not bare icons: an operator who does not already know these panels exist
                        has no reason to click a lone envelope, so the entry points went unnoticed. The
                        label collapses to the icon under `sm` where the takeover buttons need the room. */}
                    <div className="flex min-w-0 items-center gap-1">
                        {/* Broker emails for this session (view threads + reply as the owner). */}
                        <Button variant="ghost" size="sm" icon={<Mail className="h-4 w-4" />} onClick={() => setEmailsOpen(true)} aria-label="Emails">
                            <span className="hidden sm:inline">Emails</span>
                        </Button>
                        {/* This owner's other conversations — the history behind the session an operator
                            is about to pick up. Hidden without a handler (the pane owns selection). */}
                        {onSelectSession && (
                            <Button
                                variant="ghost"
                                size="sm"
                                icon={<MessagesSquare className="h-4 w-4" />}
                                onClick={() => setOtherSessionsOpen(true)}
                                aria-label="Other conversations"
                            >
                                <span className="hidden whitespace-nowrap sm:inline">Other conversations</span>
                            </Button>
                        )}
                    </div>
                    <div className="flex min-w-0 items-center gap-2">
                        {takeover && !mine && (
                            <span className="max-w-[130px] truncate text-xs font-medium text-amber-600 dark:text-amber-400">
                                {takeover.dispatcherName} is helping
                            </span>
                        )}
                        {mine ? (
                            <Button size="sm" variant="danger-soft" loading={busy} onClick={onHandback} className="whitespace-nowrap">
                                Hand back to AI
                            </Button>
                        ) : (
                            <Button size="sm" variant="primary" loading={busy} onClick={onTakeover} className="whitespace-nowrap">
                                {takeover ? 'Take over instead' : 'Take over'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Messages — centered reading column on a soft canvas so bubbles read cleanly on a wide pane. */}
            <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto bg-slate-50/60 px-4 py-5 dark:bg-slate-900/40">
                {loading && !messages?.length ? (
                    <div className="flex h-full items-center justify-center">
                        <Spinner />
                    </div>
                ) : (
                    <div className={`mx-auto flex w-full max-w-3xl flex-col gap-4 ${hasRealContent ? '' : 'min-h-full justify-center'}`}>
                        {loadingOlder && (
                            <div className="flex justify-center py-1">
                                <Spinner />
                            </div>
                        )}
                        {(messages ?? []).map((m) =>
                            m.loads?.length ? (
                                <ThreadLoads key={m.id} loads={m.loads} pickCount={m.pickCount} selectedIds={selectedIds} onToggle={toggleSelect} />
                            ) : (
                                <Bubble key={m.id} message={m} tabId={tabId} readMark={readMark} />
                            )
                        )}
                        {/* The agent is mid-turn: the driver watches a spinner on the phone, so the operator
                            gets the same beat here rather than a silent thread. Cleared by the reply. */}
                        {agentActivity && (
                            <div className="flex items-center gap-2 py-1 text-xs text-slate-500 dark:text-slate-400">
                                <span className="flex gap-1">
                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:-0.3s]" />
                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:-0.15s]" />
                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500" />
                                </span>
                                {agentActivity.message}
                            </div>
                        )}
                        {/* Only presence banners so far (no driver/AI turns) — a gentle centered hint beats a blank pane. */}
                        {!hasRealContent && (
                            <div className="pt-2 text-center text-xs text-slate-400 dark:text-slate-500">No messages in this conversation yet.</div>
                        )}
                        <div ref={bottomRef} />
                    </div>
                )}
            </div>

            {/* Load action bar — appears while loads are selected; fires an AI-invoked action. */}
            {selectedIds.size > 0 && (
                <div className="border-t border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-slate-700 dark:bg-slate-800/50">
                    <div className="mx-auto flex w-full max-w-3xl flex-wrap items-center gap-2">
                        <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[11px] font-semibold text-white">
                                {selectedIds.size}
                            </span>
                            load{selectedIds.size > 1 ? 's' : ''} selected
                        </span>
                        {LOAD_ACTIONS.map(({ label, Icon }) => (
                            <Button
                                key={label}
                                size="sm"
                                variant="secondary"
                                loading={actionBusy}
                                onClick={() => sendAction(label)}
                                icon={<Icon className="h-3.5 w-3.5" />}
                            >
                                {label}
                            </Button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setSelectedIds(new Set())}
                            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            <X className="h-3.5 w-3.5" />
                            Clear
                        </button>
                    </div>
                </div>
            )}

            <MessageComposer tabId={tabId} canReply={mine} onRequestTakeover={onTakeover} takingOver={busy} />

            <EmailsPanel tabId={tabId} open={emailsOpen} onClose={() => setEmailsOpen(false)} />
            {onSelectSession && (
                <OwnerSessionsPanel
                    ownerId={session.userId}
                    currentTabId={tabId}
                    open={otherSessionsOpen}
                    onClose={() => setOtherSessionsOpen(false)}
                    onSelect={onSelectSession}
                />
            )}
        </div>
    )
}
